// SCR-STAFF-311 — کانال‌های خارجی و نگاشت (US-SCH-080 تا 083؛ AP-SCH-03): اتصال مرکز به Endpoint مارکت‌پلیس INT (پیکربندی و اعتبارنامه فقط در
// SCR-STAFF-1000)، نگاشت ارائه‌دهنده ↔ پروفایل کانال و خدمت ↔ خدمت کانال با سهمیه درصدی ظرفیت روزانه (BR-SCH-083)، وضعیت همگام‌سازی (آخرین دریافت، همگام‌سازی
// کامل و تطبیق با تعداد اختلاف)، همگام‌سازی دستی، توقف و ادامه، گزارش همگام‌سازی و رزروهای کانال. Adapter نمونه همیشه نشان «نمونه» دارد (CLAUDE.md §10).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ChannelBookingView,
  type ChannelEndpointOption,
  type ChannelSyncLogView,
  type ExternalChannelView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FaNumber,
  FormDialog,
  JalaliDate,
  ListPage,
  SampleBadge,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useActiveBranch, useBookingOptions } from './common';

export const SCR_STAFF_311 = 'SCR-STAFF-311';

/** ردیف فرم نگاشت (ارائه‌دهنده یا خدمت). */
export interface MappingRow {
  id: string;
  name: string;
  enabled: boolean;
  externalRef: string;
  /** فقط ارائه‌دهنده: درصد سهمیه (خالی = بدون سهمیه). */
  quota: string;
}

/** خطای اعتبارسنجی فرم نگاشت (کلید i18n) یا تهی. */
export function validateMappings(
  providers: readonly MappingRow[],
  services: readonly MappingRow[],
): string | null {
  const enabled = [...providers, ...services].filter((row) => row.enabled);
  if (enabled.some((row) => row.externalRef.trim().length === 0 || row.externalRef.trim().length > 100)) {
    return 'sch:channels.mapping.ref_required';
  }
  for (const group of [providers, services]) {
    const refs = group.filter((row) => row.enabled).map((row) => row.externalRef.trim());
    if (new Set(refs).size !== refs.length) {
      return 'sch:channels.mapping.ref_duplicate';
    }
  }
  if (
    providers.some((row) => {
      if (!row.enabled || row.quota.trim() === '') {
        return false;
      }
      const value = Number(row.quota);
      return !Number.isInteger(value) || value < 1 || value > 100;
    })
  ) {
    return 'sch:channels.mapping.quota_invalid';
  }
  return null;
}

/** بدنه PUT نگاشت از ردیف‌های فرم. */
export function mappingsBody(
  providers: readonly MappingRow[],
  services: readonly MappingRow[],
  rowVersion: number,
) {
  return {
    providers: providers
      .filter((row) => row.enabled)
      .map((row) => ({
        providerId: row.id,
        externalRef: row.externalRef.trim(),
        quotaPercent: row.quota.trim() === '' ? null : Number(row.quota),
      })),
    services: services
      .filter((row) => row.enabled)
      .map((row) => ({ serviceId: row.id, externalRef: row.externalRef.trim() })),
    rowVersion,
  };
}

function useChannelsKey() {
  const { session } = useTenantSession();
  return ['sch', session?.tenantId, 'booking-channels'] as const;
}

export function BookingChannelsPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, activeTenant } = useTenantSession();
  const write = useWriteAccess('sch.channel.manage');
  const queryClient = useQueryClient();
  const key = useChannelsKey();
  const [connectOpen, setConnectOpen] = useState(false);
  const [mappingFor, setMappingFor] = useState<ExternalChannelView | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const channels = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/channels')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const sync = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/scheduling/channels/{id}:sync', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (channel: ExternalChannelView) =>
      unwrap(
        tenantApi.PUT('/api/v1/scheduling/channels/{id}', {
          params: { path: { id: channel.id } },
          body: {
            name: channel.name,
            status: channel.status === 'Active' ? 'Paused' : 'Active',
            rowVersion: channel.rowVersion,
          },
        }),
      ),
    onSuccess: refresh,
  });
  const branchName = (id: string) =>
    activeTenant?.branches.find((branch) => branch.branchId === id)?.name ?? '';
  const current = channels.data?.items.find((item) => item.id === selected) ?? null;

  const columns: DataTableColumn<ExternalChannelView>[] = [
    {
      id: 'name',
      headerKey: 'sch:channels.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{row.name}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'branch', headerKey: 'sch:channels.branch', cell: (row) => branchName(row.branchId) },
    {
      id: 'status',
      headerKey: 'sch:channels.status',
      cell: (row) => (
        <Chip
          size="small"
          color={row.status === 'Active' ? 'success' : 'default'}
          label={t(`sch:channels.state.${row.status}`)}
        />
      ),
    },
    {
      id: 'mapping',
      headerKey: 'sch:channels.mapping_summary',
      cell: (row) =>
        t('sch:channels.mapping_count', {
          providers: faDigits(row.providers.length),
          services: faDigits(row.services.length),
        }),
    },
    {
      id: 'sync',
      headerKey: 'sch:channels.last_sync',
      cell: (row) => (
        <Stack spacing={0.5}>
          <span>
            {t('sch:channels.last_pull')}:{' '}
            {row.lastPulledAt ? <JalaliDate value={row.lastPulledAt} withTime /> : '—'}
          </span>
          <span>
            {t('sch:channels.last_reconcile')}:{' '}
            {row.lastReconciledAt ? <JalaliDate value={row.lastReconciledAt} withTime /> : '—'}
            {Number(row.lastDriftCount) > 0 ? (
              <Chip
                size="small"
                color="warning"
                sx={{ ms: 1 }}
                label={t('sch:channels.drift', { count: faDigits(row.lastDriftCount) })}
              />
            ) : null}
          </span>
          {row.lastErrorCode ? (
            <Chip size="small" color="error" label={t('sch:channels.error', { code: row.lastErrorCode })} />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'stats',
      headerKey: 'sch:channels.stats',
      cell: (row) =>
        t('sch:channels.stats_value', {
          booked: faDigits(row.stats.booked),
          conflicts: faDigits(row.stats.conflicts),
          rejected: faDigits(row.stats.rejected),
          cancelled: faDigits(row.stats.cancelled),
        }),
    },
    {
      id: 'actions',
      headerKey: 'sch:channels.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setSelected(row.id)} data-testid={`sch-channel-open-${row.id}`}>
            {t('sch:channels.details')}
          </Button>
          <Button
            size="small"
            disabled={!write.canWrite}
            onClick={() => setMappingFor(row)}
            data-testid={`sch-channel-map-${row.id}`}
          >
            {t('sch:channels.edit_mapping')}
          </Button>
          <Button
            size="small"
            disabled={!write.canWrite || row.status !== 'Active' || sync.isPending}
            onClick={() => sync.mutate(row.id)}
            data-testid={`sch-channel-sync-${row.id}`}
          >
            {t('sch:channels.sync_now')}
          </Button>
          <Button
            size="small"
            color={row.status === 'Active' ? 'warning' : 'success'}
            disabled={!write.canWrite || toggle.isPending}
            onClick={() => toggle.mutate(row)}
            data-testid={`sch-channel-toggle-${row.id}`}
          >
            {t(row.status === 'Active' ? 'sch:channels.pause' : 'sch:channels.resume')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_311}
      titleKey="sch:channels.title"
      actions={
        <Button
          variant="contained"
          disabled={!write.canWrite}
          onClick={() => setConnectOpen(true)}
          data-testid="sch-channel-connect"
        >
          {t('sch:channels.connect')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <Alert severity="info" variant="outlined">
            {t('sch:channels.note')}{' '}
            <Link component={RouterLink} to="/settings/integrations">
              {t('sch:channels.configure_endpoints')}
            </Link>
          </Alert>
          {sync.data ? (
            <Alert severity="success" data-testid="sch-channel-sync-result">
              {t('sch:channels.sync_result', {
                queued: faDigits(sync.data.queued),
                received: faDigits(sync.data.received),
                drift: faDigits(sync.data.drift),
              })}
            </Alert>
          ) : null}
          {sync.error ? <ErrorState error={sync.error} compact /> : null}
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        columns={columns}
        rows={channels.data?.items}
        getRowId={(row) => row.id}
        isLoading={channels.isPending}
        error={channels.error}
        onRetry={() => void channels.refetch()}
        emptyTitleKey="sch:channels.empty"
        emptyDescriptionKey="sch:channels.empty_hint"
        captionKey="sch:channels.title"
      />
      {current ? <ChannelDetail channel={current} /> : null}
      {connectOpen ? <ConnectDialog onClose={() => setConnectOpen(false)} onDone={refresh} /> : null}
      {mappingFor ? (
        <MappingDialog channel={mappingFor} onClose={() => setMappingFor(null)} onDone={refresh} />
      ) : null}
    </ListPage>
  );
}

function ConnectDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('sch');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const { branchId } = useActiveBranch();
  const [name, setName] = useState('');
  const [endpointId, setEndpointId] = useState('');
  const [branch, setBranch] = useState(branchId ?? '');
  const [invalid, setInvalid] = useState(false);
  const endpoints = useQuery({
    queryKey: ['sch', session?.tenantId, 'booking-channel-endpoints'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/channels/endpoints')),
  });
  const available: ChannelEndpointOption[] = (endpoints.data?.items ?? []).filter(
    (item) => item.channelId === null && item.allowed,
  );
  const connect = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/channels', {
          body: { name: name.trim(), endpointId, branchId: branch },
        }),
      ),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const submit = () => {
    if (name.trim() === '' || endpointId === '' || branch === '') {
      setInvalid(true);
      return;
    }
    connect.mutate();
  };

  return (
    <FormDialog
      open
      titleKey="sch:channels.connect_title"
      screenId={SCR_STAFF_311}
      onSubmit={submit}
      onClose={onClose}
      isDirty={name !== '' || endpointId !== ''}
      isSubmitting={connect.isPending}
      submitError={connect.error}
      submitKey="sch:channels.connect"
    >
      {endpoints.isSuccess && available.length === 0 ? (
        <Alert severity="warning" data-testid="sch-channel-no-endpoint">
          {t('sch:channels.no_endpoint')}{' '}
          <Link component={RouterLink} to="/settings/integrations">
            {t('sch:channels.configure_endpoints')}
          </Link>
        </Alert>
      ) : null}
      <TextField
        label={t('sch:channels.name')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        error={invalid && name.trim() === ''}
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'sch-channel-name' } }}
      />
      <TextField
        select
        label={t('sch:channels.endpoint')}
        value={endpointId}
        onChange={(event) => setEndpointId(event.target.value)}
        required
        error={invalid && endpointId === ''}
        slotProps={{ select: { SelectDisplayProps: { 'data-testid': 'sch-channel-endpoint' } as never } }}
      >
        {available.map((item) => (
          <MenuItem key={item.endpointId} value={item.endpointId}>
            {t(`int:adapter.${item.adapterCode}`, { defaultValue: item.adapterCode })}
            {item.isSample ? ` (${t('common:sample.badge')})` : ''}
            {item.isActive ? '' : ` — ${t('sch:channels.endpoint_inactive')}`}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('sch:channels.branch')}
        value={branch}
        onChange={(event) => setBranch(event.target.value)}
        required
        error={invalid && branch === ''}
      >
        {(activeTenant?.branches ?? []).map((item) => (
          <MenuItem key={item.branchId} value={item.branchId}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      {invalid ? <Alert severity="error">{t('sch:channels.required')}</Alert> : null}
    </FormDialog>
  );
}

function MappingDialog({
  channel,
  onClose,
  onDone,
}: {
  channel: ExternalChannelView;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('sch');
  const { tenantApi } = useTenantSession();
  const options = useBookingOptions(channel.branchId);
  const [providers, setProviders] = useState<MappingRow[] | null>(null);
  const [services, setServices] = useState<MappingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // فهرست اولیه حداکثر ۱۰۰ خدمت فعال است؛ جستجو خدمات بیشتر را به فهرست می‌افزاید و انتخاب‌های قبلی می‌مانند (C-AGENT-1539).
  const [serviceQuery, setServiceQuery] = useState('');
  const deferredQuery = useDeferredValue(serviceQuery.trim());
  const searched = useBookingOptions(channel.branchId, null, deferredQuery.length > 0, deferredQuery);
  useEffect(() => {
    const found = searched.data?.services;
    if (!found || found.length === 0) {
      return;
    }
    setServices((current) => {
      if (current === null) {
        return current;
      }
      const added = found
        .filter((option) => !current.some((row) => row.id === option.id))
        .map((option) => ({ id: option.id, name: option.name, enabled: false, externalRef: '', quota: '' }));
      return added.length === 0 ? current : [...added, ...current];
    });
  }, [searched.data]);

  if (options.data && providers === null && services === null) {
    const providerRows = options.data.providers.map((option) => {
      const mapped = channel.providers.find((item) => item.providerId === option.id);
      return {
        id: option.id,
        name: option.name ?? '',
        enabled: mapped !== undefined,
        externalRef: mapped?.externalRef ?? '',
        quota: mapped?.quotaPercent ? String(mapped.quotaPercent) : '',
      };
    });
    // نگاشت ارائه‌دهنده یا خدمتی که دیگر در گزینه‌ها نیست حفظ می‌شود.
    for (const mapped of channel.providers.filter(
      (item) => !providerRows.some((row) => row.id === item.providerId),
    )) {
      providerRows.push({
        id: mapped.providerId,
        name: mapped.providerName ?? mapped.externalRef,
        enabled: true,
        externalRef: mapped.externalRef,
        quota: mapped.quotaPercent ? String(mapped.quotaPercent) : '',
      });
    }
    const serviceRows = options.data.services.map((option) => {
      const mapped = channel.services.find((item) => item.serviceId === option.id);
      return {
        id: option.id,
        name: option.name,
        enabled: mapped !== undefined,
        externalRef: mapped?.externalRef ?? '',
        quota: '',
      };
    });
    for (const mapped of channel.services.filter(
      (item) => !serviceRows.some((row) => row.id === item.serviceId),
    )) {
      serviceRows.push({
        id: mapped.serviceId,
        name: mapped.serviceName ?? mapped.externalRef,
        enabled: true,
        externalRef: mapped.externalRef,
        quota: '',
      });
    }
    setProviders(providerRows);
    setServices(serviceRows);
  }

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/scheduling/channels/{id}/mappings', {
          params: { path: { id: channel.id } },
          body: mappingsBody(providers ?? [], services ?? [], Number(channel.rowVersion)),
        }),
      ),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const submit = () => {
    const problem = validateMappings(providers ?? [], services ?? []);
    setError(problem);
    if (problem === null) {
      save.mutate();
    }
  };
  const update = (kind: 'provider' | 'service', id: string, patch: Partial<MappingRow>) => {
    const apply = (rows: MappingRow[] | null) =>
      (rows ?? []).map((row) => (row.id === id ? { ...row, ...patch } : row));
    if (kind === 'provider') {
      setProviders(apply(providers));
    } else {
      setServices(apply(services));
    }
  };
  const rows = (kind: 'provider' | 'service', items: MappingRow[]) =>
    items.map((row) => (
      <Stack
        key={row.id}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <FormControlLabel
          sx={{ minWidth: 200 }}
          control={
            <Checkbox
              checked={row.enabled}
              onChange={(event) => update(kind, row.id, { enabled: event.target.checked })}
              slotProps={{ input: { 'data-testid': `sch-map-${kind}-${row.id}` } as never }}
            />
          }
          label={row.name}
        />
        <TextField
          size="small"
          label={t(
            kind === 'provider' ? 'sch:channels.mapping.provider_ref' : 'sch:channels.mapping.service_ref',
          )}
          value={row.externalRef}
          disabled={!row.enabled}
          onChange={(event) => update(kind, row.id, { externalRef: event.target.value })}
          slotProps={{ htmlInput: { maxLength: 100, 'data-testid': `sch-map-${kind}-ref-${row.id}` } }}
        />
        {kind === 'provider' ? (
          <TextField
            size="small"
            type="number"
            label={t('sch:channels.mapping.quota')}
            helperText={t('sch:channels.mapping.quota_hint')}
            value={row.quota}
            disabled={!row.enabled}
            onChange={(event) => update(kind, row.id, { quota: event.target.value })}
            slotProps={{ htmlInput: { min: 1, max: 100, 'data-testid': `sch-map-quota-${row.id}` } }}
          />
        ) : null}
      </Stack>
    ));

  return (
    <FormDialog
      open
      titleKey="sch:channels.mapping.title"
      screenId={SCR_STAFF_311}
      onSubmit={submit}
      onClose={onClose}
      isDirty={providers !== null}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <Alert severity="info" variant="outlined">
        {t('sch:channels.mapping.note')}
      </Alert>
      {options.error ? <ErrorState error={options.error} compact /> : null}
      <Typography variant="subtitle2" component="h3">
        {t('sch:channels.mapping.providers')}
      </Typography>
      {rows('provider', providers ?? [])}
      <Typography variant="subtitle2" component="h3">
        {t('sch:channels.mapping.services')}
      </Typography>
      <TextField
        size="small"
        label={t('sch:channels.mapping.service_search')}
        value={serviceQuery}
        onChange={(event) => setServiceQuery(event.target.value)}
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'sch-map-service-search' } }}
      />
      {rows('service', services ?? [])}
      {error ? (
        <Alert severity="error" data-testid="sch-map-error">
          {t(error)}
        </Alert>
      ) : null}
    </FormDialog>
  );
}

function ChannelDetail({ channel }: { channel: ExternalChannelView }) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const [tab, setTab] = useState<'log' | 'bookings'>('log');
  const log = useQuery({
    queryKey: ['sch', session?.tenantId, 'booking-channel-log', channel.id],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/channels/{id}/sync-log', {
          params: { path: { id: channel.id }, query: { limit: 50 } },
        }),
      ),
    enabled: tab === 'log',
  });
  const bookings = useQuery({
    queryKey: ['sch', session?.tenantId, 'booking-channel-bookings', channel.id],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/channels/{id}/bookings', {
          params: { path: { id: channel.id }, query: { limit: 50 } },
        }),
      ),
    enabled: tab === 'bookings',
  });
  const logColumns: DataTableColumn<ChannelSyncLogView>[] = [
    { id: 'at', headerKey: 'sch:channels.log.at', cell: (row) => <JalaliDate value={row.at} withTime /> },
    { id: 'kind', headerKey: 'sch:channels.log.kind', cell: (row) => t(`sch:channels.log.kind_${row.kind}`) },
    {
      id: 'success',
      headerKey: 'sch:channels.log.result',
      cell: (row) => (
        <Chip
          size="small"
          color={row.success ? 'success' : 'error'}
          label={t(row.success ? 'sch:channels.log.ok' : 'sch:channels.log.failed')}
        />
      ),
    },
    { id: 'items', headerKey: 'sch:channels.log.items', cell: (row) => <FaNumber value={row.items} /> },
    { id: 'drift', headerKey: 'sch:channels.log.drift', cell: (row) => <FaNumber value={row.drift} /> },
    {
      id: 'detail',
      headerKey: 'sch:channels.log.detail',
      cell: (row) => <bdi dir="ltr">{row.detail ?? ''}</bdi>,
    },
  ];
  const bookingColumns: DataTableColumn<ChannelBookingView>[] = [
    {
      id: 'external',
      headerKey: 'sch:channels.bookings.external_id',
      cell: (row) => <bdi dir="ltr">{row.externalId}</bdi>,
    },
    {
      id: 'start',
      headerKey: 'sch:channels.bookings.start',
      cell: (row) => <JalaliDate value={row.startsAt} withTime />,
    },
    {
      id: 'status',
      headerKey: 'sch:channels.bookings.status',
      cell: (row) => (
        <span>
          {t(`sch:channels.bookings.status_${row.status}`)}
          {row.cancelSource ? ` (${t(`sch:channels.bookings.source_${row.cancelSource}`)})` : ''}
        </span>
      ),
    },
    {
      id: 'reason',
      headerKey: 'sch:channels.bookings.reason',
      cell: (row) => (row.reason ? t(`sch:channels.reason.${row.reason}`, { defaultValue: row.reason }) : ''),
    },
    {
      id: 'appointment',
      headerKey: 'sch:channels.bookings.appointment',
      cell: (row) =>
        row.appointmentId ? (
          <Link component={RouterLink} to={`/appointments/${row.appointmentId}`}>
            {t('sch:channels.bookings.open')}
          </Link>
        ) : (
          ''
        ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }} data-testid="sch-channel-detail">
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {channel.name}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, value: 'log' | 'bookings') => setTab(value)}
        aria-label={t('sch:channels.detail_tabs')}
      >
        <Tab value="log" label={t('sch:channels.log.title')} data-testid="sch-channel-tab-log" />
        <Tab
          value="bookings"
          label={t('sch:channels.bookings.title')}
          data-testid="sch-channel-tab-bookings"
        />
      </Tabs>
      {tab === 'log' ? (
        <DataTable
          columns={logColumns}
          rows={log.data?.items}
          getRowId={(row) => row.id}
          isLoading={log.isPending}
          error={log.error}
          onRetry={() => void log.refetch()}
          emptyTitleKey="sch:channels.log.empty"
          captionKey="sch:channels.log.title"
        />
      ) : (
        <DataTable
          columns={bookingColumns}
          rows={bookings.data?.items}
          getRowId={(row) => row.id}
          isLoading={bookings.isPending}
          error={bookings.error}
          onRetry={() => void bookings.refetch()}
          emptyTitleKey="sch:channels.bookings.empty"
          captionKey="sch:channels.bookings.title"
        />
      )}
    </Paper>
  );
}
